import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';

export interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    userId: number;
    token: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    isAdmin: boolean;
    privacyAgreementAccepted: boolean;
    requiresPrivacyAgreement: boolean;
  };
}

export interface SignUpRequest {
  email: string | null;
  mobileNumber: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  isDeceased: boolean;
  qualification?: string;
  profession?: string;
  parentReferralCode?: string;
  relationshipType?: string;
  username: string;
  password: string;
  acceptPrivacyAgreement: boolean;
  isAdminCreated: boolean;
}

export interface SignUpResponse {
  success: boolean;
  message: string;
  username: string;
  password: string;
  referralCode: string;
  privacyAgreementAccepted: boolean;
  isAdminCreated: boolean;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordOtpRequest {
  mobileNumber: string;
}

export interface VerifyOtpRequest {
  mobileNumber: string;
  otpCode: string;
  newPassword: string;
}

export interface GoogleAuthRequest {
  idToken: string;
  parentReferralCode?: string;
  spouseReferralCode?: string;
  acceptPrivacyAgreement: boolean;
}

export interface GoogleAuthResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    userId: number;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    isAdmin: boolean;
    isNewUser: boolean;
    privacyAgreementAccepted: boolean;
    requiresPrivacyAgreement: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private http: HttpClient) {
    this.loadUserFromStorage();
  }

  private loadUserFromStorage(): void {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        this.currentUserSubject.next(user);
      } catch (error) {
        this.logout();
      }
    }
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.apiUrl}/api/auth/login`, credentials)
      .pipe(
        tap((response: any) => {
          if (response.success) {
            const user: User = {
              id: response.data.userId,
              username: response.data.username,
              email: response.data.email,
              firstName: response.data.firstName,
              lastName: response.data.lastName,
              isAdmin: response.data.isAdmin
            };
            
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(user));
            this.currentUserSubject.next(user);
          }
        })
      );
  }

  signup(userData: SignUpRequest): Observable<SignUpResponse> {
    return this.http.post<SignUpResponse>(`${environment.apiUrl}/api/auth/signup`, userData);
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/api/auth/forgot-password`, { email });
  }

  requestPasswordResetOtp(mobileNumber: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/api/auth/reset-password-otp`, { mobileNumber });
  }

  verifyOtpAndResetPassword(mobileNumber: string, otpCode: string, newPassword: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/api/auth/verify-otp-reset-password`, {
      mobileNumber,
      otpCode,
      newPassword
    });
  }

  acceptPrivacyAgreement(acceptPrivacyAgreement: boolean, privacyAgreementVersion?: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/api/auth/accept-privacy-agreement`, {
      acceptPrivacyAgreement,
      privacyAgreementVersion: privacyAgreementVersion || '1.0'
    });
  }

  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    this.currentUserSubject.next(null);
  }

  getToken(): string | null {
    return localStorage.getItem('token');
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    // Check if token is expired (basic JWT expiration check)
    try {
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      
      if (tokenPayload.exp && tokenPayload.exp < currentTime) {
        this.logout();
        return false;
      }

      return true;
    } catch (error) {
      this.logout();
      return false;
    }
  }

  isAdmin(): boolean {
    const user = this.currentUserSubject.value;
    return user?.isAdmin || false;
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Check authentication status and redirect to login if not authenticated
   * @param router - Angular Router instance
   * @param currentUrl - Current URL to redirect back to after login
   * @returns boolean - true if authenticated, false if redirected
   */
  checkAuthAndRedirect(router: any, currentUrl?: string): boolean {
    if (!this.isAuthenticated()) {
      router.navigate(['/login'], { 
        queryParams: { 
          reason: 'no_token',
          returnUrl: currentUrl || router.url 
        } 
      });
      return false;
    }
    return true;
  }

  /**
   * Get hierarchy data for the current logged-in user
   * This method is accessible to all authenticated users (not just admins)
   */
  getUserHierarchy(): Observable<any> {
    const currentUser = this.getCurrentUser();
    if (!currentUser) {
      throw new Error('No current user found');
    }
    return this.http.get<any>(`${environment.apiUrl}/api/relative/${currentUser.id}/hierarchy`);
  }

  googleAuth(request: GoogleAuthRequest): Observable<GoogleAuthResponse> {
    console.log('📡 Sending Google auth request to:', `${environment.apiUrl}/api/auth/google-auth`);
    return this.http.post<GoogleAuthResponse>(`${environment.apiUrl}/api/auth/google-auth`, request)
      .pipe(
        tap((response: any) => {
          console.log('📥 Google auth response received:', response);
          if (response && response.success) {
            const user: User = {
              id: response.data.userId,
              username: response.data.username,
              email: response.data.email,
              firstName: response.data.firstName,
              lastName: response.data.lastName,
              isAdmin: response.data.isAdmin
            };
            
            console.log('💾 Storing auth data:', { token: !!response.data.token, user });
            localStorage.setItem('token', response.data.token);
            localStorage.setItem('user', JSON.stringify(user));
            this.currentUserSubject.next(user);
            console.log('✅ Auth data stored successfully');
          } else {
            console.warn('⚠️ Response does not indicate success:', response);
          }
        })
      );
  }

  getCurrentUserId(): number | null {
    const user = this.getCurrentUser();
    return user?.id || null;
  }

  /**
   * Get public list of relatives for signup (optional - may require authentication)
   * This is used during signup to optionally select parent/spouse
   * Falls back to public endpoint if available, otherwise handles gracefully
   */
  getPublicRelatives(search?: string): Observable<any> {
    // Try public endpoint first, fallback to regular endpoint if needed
    let url = `${environment.apiUrl}/api/auth/public-relatives`;
    if (search) {
      url += `?search=${encodeURIComponent(search)}`;
    }
    return this.http.get<any>(url);
  }
}
